import { useLang } from "@/context/LanguageContext";
import { motion } from "framer-motion";
import { CheckCircle, Quotes } from "@phosphor-icons/react";

const ABOUT_IMG = "https://images.pexels.com/photos/260689/pexels-photo-260689.jpeg?auto=compress&cs=tinysrgb&w=800";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" },
  }),
};

export default function About() {
  const { t } = useLang();

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#0D9488] mb-4"
          >
            {t.about.overline}
          </motion.p>
          <motion.h1
            initial="hidden"
            animate="visible"
            custom={1}
            variants={fadeUp}
            className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#0A192F] max-w-4xl"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t.about.headline}
          </motion.h1>
        </div>
      </section>

      {/* Intro + Image */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-8">
                {t.about.intro}
              </p>
              <h3 className="text-xl md:text-2xl font-medium text-[#0A192F] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {t.about.mission.title}
              </h3>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed">
                {t.about.mission.text}
              </p>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={2}
              variants={fadeUp}
            >
              <img
                src={ABOUT_IMG}
                alt="GJC Enertec Office"
                className="w-full h-auto object-cover shadow-sm"
                data-testid="about-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quote Block */}
      <section className="py-20 md:py-28 bg-[#0A192F]" data-testid="about-quote-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Quotes size={40} weight="fill" className="text-[#0D9488] mx-auto mb-6" />
          <motion.blockquote
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-lg md:text-xl text-white leading-relaxed italic"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            "{t.about.quote}"
          </motion.blockquote>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {t.about.values.map((value, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                className="flex gap-4 p-6 border border-slate-200 hover:shadow-md transition-all duration-300 hover:-translate-y-1"
                data-testid={`about-value-${i}`}
              >
                <CheckCircle size={28} weight="duotone" className="text-[#0D9488] shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-medium text-[#0A192F] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {value.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{value.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Full */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="about-leadership-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#0D9488] mb-4"
          >
            {t.leadership.overline}
          </motion.p>
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
            variants={fadeUp}
            className="text-3xl md:text-4xl font-medium tracking-tight text-[#0A192F] mb-16"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t.leadership.title}
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.leadership.members.map((member, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                className="bg-white border border-slate-200 p-8 shadow-sm"
                data-testid={`about-leader-${i}`}
              >
                <div className="w-16 h-16 bg-[#0A192F] text-white flex items-center justify-center text-xl font-medium mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {member.name.split(" ").map((n) => n[0]).join("")}
                </div>
                <h3 className="text-lg font-medium text-[#0A192F]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {member.name}
                </h3>
                <p className="text-sm font-medium text-[#0D9488] mb-3">{member.role}</p>
                <p className="text-sm text-slate-600 leading-relaxed">{member.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
